import { useSyncExternalStore } from 'react';






export type ToastKind = 'ok' | 'err' | 'wa' | (string & {});

type ToastItem = {
  id: number;
  msg: string;
  kind?: ToastKind;
  undo?: () => void;
  leaving: boolean;
};

const UNDO_TTL = 7000;
const FADE_MS = 260;

function readingTtl(msg: string, kind?: ToastKind): number {
  const base = kind === 'err' ? 6000 : kind === 'wa' ? 5000 : 3500;
  return Math.min(12000, base + msg.length * 45);
}

const KIND_LABEL: Record<string, string> = { ok: 'Готово', err: 'Ошибка', wa: 'Внимание' };

const ICONS: Record<string, string> = {
  ok: 'M5 12.5l4.5 4.5L19 7.5',
  err: 'M7 7l10 10M17 7L7 17',
  wa: 'M12 7v6M12 16.5v.5',
  info: 'M12 11v6M12 7.5v.5',
};

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getSnapshot(): ToastItem[] {
  return items;
}

function patch(id: number, p: Partial<ToastItem>): void {
  items = items.map((t) => (t.id === id ? { ...t, ...p } : t));
  emit();
}

function dismiss(id: number): void {
  const t = items.find((x) => x.id === id);
  if (!t || t.leaving) return;
  patch(id, { leaving: true });
  setTimeout(() => {
    items = items.filter((x) => x.id !== id);
    emit();
  }, FADE_MS);
}

function push(item: Omit<ToastItem, 'id' | 'leaving'>, ttl: number): number {
  const id = ++seq;
  items = [...items, { ...item, id, leaving: false }];
  emit();
  setTimeout(() => dismiss(id), ttl);
  return id;
}

export function toast(msg: string, kind?: ToastKind): void {
  push({ msg, kind }, readingTtl(msg, kind));
}

export function undoToast(msg: string, restore: () => void): void {
  push({ msg, undo: restore }, UNDO_TTL);
}

export function ToastHost() {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return (
    <div id="toasts" aria-live="polite">
      {list.map((t) => {
        const kind = t.kind && ICONS[t.kind] ? t.kind : 'info';
        return (
          <div
            key={t.id}
            role={kind === 'err' ? 'alert' : 'status'}
            className={`toast ${kind}`}
            style={t.leaving ? { opacity: 0, transition: 'opacity .25s' } : undefined}
          >
            <span className="toast-ico" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d={ICONS[kind]} />
              </svg>
            </span>
            <div className="toast-body">
              {KIND_LABEL[kind] ? <b className="toast-title">{KIND_LABEL[kind]}</b> : null}
              <span className="toast-msg">{t.msg}</span>
              {t.undo ? (
                <button
                  className="toast-undo"
                  type="button"
                  onClick={() => {
                    if (t.leaving) return;
                    t.undo?.();
                    dismiss(t.id);
                    toast('Действие отменено', 'ok');
                  }}
                >
                  Отменить
                </button>
              ) : null}
            </div>
            <button type="button" className="toast-close" aria-label="Закрыть" onClick={() => dismiss(t.id)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M7 7l10 10M17 7L7 17" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
